import React, { useState } from "react";
import { useInquiries } from "../index";
import { NewInquiryForm } from "./NewInquiryForm";

/**
 * SupportInquiry - 고객 문의 탭 콘텐츠 컴포넌트
 * 모바일 최적화: 모바일에서는 카드 형식, 데스크톱에서는 테이블 형식
 */
export const SupportInquiry = () => {
  const [showNewInquiry, setShowNewInquiry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pageSize = 10;

  // 문의 API 연동
  const { inquiriesList, isLoading, error, refetch } = useInquiries();

  // 페이지네이션 계산
  const totalPages = Math.ceil((inquiriesList.length || 0) / pageSize);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedList = inquiriesList.slice(startIdx, endIdx);

  return (
    <div className="w-full flex flex-col gap-8 md:gap-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="text-2xl md:text-4xl font-bold text-black">고객 문의</h2>
        {/* Write Button - Mobile */}
        <button
          onClick={() => setShowNewInquiry(true)}
          className="md:hidden w-full px-4 py-3 bg-orange-500 text-white rounded hover:bg-orange-600 font-medium transition-colors"
        >
          글쓰기
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <p className="text-gray-500">문의사항을 불러오는 중...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex justify-center items-center py-12">
          <p className="text-red-500">문의사항을 불러올 수 없습니다. {error}</p>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && (
        <div className="w-full flex flex-col gap-6">
          {/* Desktop Table View */}
          <div className="hidden md:block w-full overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-400">
                  <th className="px-4 py-4 text-left text-sm font-medium text-gray-700 w-12">
                    No
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-medium text-gray-700 w-24">
                    상태
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-medium text-gray-700 flex-1">
                    제목
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-medium text-gray-700 w-24">
                    글쓴이
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-medium text-gray-700 w-32">
                    작성시간
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedList.length > 0 ? (
                  paginatedList.map((inquiry: any, index: number) => (
                    <React.Fragment key={inquiry.id}>
                      <tr
                        onClick={() =>
                          setExpandedId(
                            expandedId === inquiry.id ? null : inquiry.id
                          )
                        }
                        className="border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {inquiriesList.length - (startIdx + index)}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium">
                          {inquiry.comment ? (
                            <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-xs">
                              답변완료
                            </span>
                          ) : (
                            <span className="text-orange-600 bg-orange-50 px-3 py-1 rounded-full text-xs">
                              답변대기
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            {inquiry.title}
                            {inquiry.is_public === false && (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5s-5 2.24-5 5v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                              </svg>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {inquiry.author_name || "익명"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {new Date(inquiry.created_at).toLocaleDateString(
                            "ko-KR"
                          )}
                        </td>
                      </tr>
                      {/* Detail Row */}
                      {expandedId === inquiry.id && (
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <td colSpan={5} className="px-4 py-4">
                            <div className="flex flex-col gap-3">
                              {/* Private Notice */}
                              {inquiry.is_public === false && (
                                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <p className="text-sm text-yellow-800 font-medium">
                                    비공개 글입니다. 관리자 및 작성자만 확인할
                                    수 있습니다.
                                  </p>
                                </div>
                              )}

                              {/* Content and Answers - Only show if public */}
                              {inquiry.is_public !== false && (
                                <>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-2">
                                      질문 내용
                                    </p>
                                    <p className="text-sm text-gray-700 leading-6">
                                      {inquiry.content}
                                    </p>
                                  </div>
                                  {inquiry.comment && (
                                    <>
                                      <div className="border-t border-gray-300 pt-3 flex items-center gap-2">
                                        <svg
                                          width="20"
                                          height="20"
                                          viewBox="0 0 20 20"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M5.41602 7.08333C5.41602 6.73816 5.69584 6.45833 6.04102 6.45833H13.9577C14.3028 6.45833 14.5827 6.73816 14.5827 7.08333C14.5827 7.42851 14.3028 7.70833 13.9577 7.70833H6.04102C5.69584 7.70833 5.41602 7.42851 5.41602 7.08333Z"
                                            fill="black"
                                            fill-opacity="0.6"
                                          />
                                          <path
                                            d="M6.04102 9.79167C5.69584 9.79167 5.41602 10.0715 5.41602 10.4167C5.41602 10.7618 5.69584 11.0417 6.04102 11.0417H13.9577C14.3028 11.0417 14.5827 10.7618 14.5827 10.4167C14.5827 10.0715 14.3028 9.79167 13.9577 9.79167H6.04102Z"
                                            fill="black"
                                            fill-opacity="0.6"
                                          />
                                          <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M1.66602 4.16667C1.66602 3.24619 2.41221 2.5 3.33268 2.5H16.666C17.5865 2.5 18.3327 3.24619 18.3327 4.16667V13.3333C18.3327 14.2538 17.5865 15 16.666 15H12.8118L10.7257 18.7088C10.4071 19.2752 9.5916 19.2752 9.27302 18.7088L7.18685 15H3.33268C2.41221 15 1.66602 14.2538 1.66602 13.3333V4.16667ZM7.18685 13.75C7.63844 13.75 8.05492 13.9936 8.27632 14.3872L9.99935 17.4503L11.7223 14.3872C11.9438 13.9936 12.3603 13.75 12.8118 13.75H16.666C16.8961 13.75 17.0827 13.5634 17.0827 13.3333V4.16667C17.0827 3.93655 16.8961 3.75 16.666 3.75H3.33268C3.10257 3.75 2.91602 3.93655 2.91602 4.16667V13.3333C2.91602 13.5634 3.10257 13.75 3.33268 13.75H7.18685Z"
                                            fill="black"
                                            fill-opacity="0.6"
                                          />
                                        </svg>
                                        <p className="text-xs font-semibold text-gray-600">
                                          그로우러닝 담당자
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-700 leading-6">
                                          {inquiry.comment}
                                        </p>
                                      </div>
                                    </>
                                  )}
                                  {!inquiry.comment && (
                                    <div className="border-t border-gray-300 pt-3">
                                      <p className="text-sm text-gray-500 italic">
                                        아직 답변이 없습니다.
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      등록된 문의가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden flex flex-col gap-3">
            {paginatedList.length > 0 ? (
              paginatedList.map((inquiry: any, index: number) => (
                <div key={inquiry.id} className="flex flex-col">
                  <button
                    onClick={() =>
                      setExpandedId(
                        expandedId === inquiry.id ? null : inquiry.id
                      )
                    }
                    className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg text-left hover:bg-gray-50 transition-colors active:bg-gray-100"
                  >
                    <div className="flex items-start justify-between gap-2">
                      {inquiry.comment ? (
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                          답변완료
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">
                          답변대기
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        No. {inquiriesList.length - (startIdx + index)}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 flex items-center gap-2">
                      {inquiry.title}
                      {inquiry.is_public === false && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="text-gray-500 flex-shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5s-5 2.24-5 5v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                        </svg>
                      )}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>{inquiry.author_name || "익명"}</span>
                      <span>
                        {new Date(inquiry.created_at).toLocaleDateString(
                          "ko-KR"
                        )}
                      </span>
                    </div>
                  </button>

                  {/* Mobile Detail View */}
                  {expandedId === inquiry.id && (
                    <div className="bg-gray-50 border border-t-0 border-gray-200 rounded-b-lg p-4 space-y-3">
                      {/* Private Notice */}
                      {inquiry.is_public === false && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800 font-medium">
                            비공개 글입니다. 관리자 및 작성자만 확인할 수
                            있습니다.
                          </p>
                        </div>
                      )}

                      {/* Content and Answers - Only show if public */}
                      {inquiry.is_public !== false && (
                        <>
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-2">
                              질문 내용
                            </p>
                            <p className="text-sm text-gray-700 leading-6 whitespace-pre-wrap">
                              {inquiry.content}
                            </p>
                          </div>
                          {inquiry.comment && (
                            <>
                              <div className="border-t border-gray-300 pt-3 flex items-center gap-2">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M5.41602 7.08333C5.41602 6.73816 5.69584 6.45833 6.04102 6.45833H13.9577C14.3028 6.45833 14.5827 6.73816 14.5827 7.08333C14.5827 7.42851 14.3028 7.70833 13.9577 7.70833H6.04102C5.69584 7.70833 5.41602 7.42851 5.41602 7.08333Z"
                                    fill="black"
                                    fill-opacity="0.6"
                                  />
                                  <path
                                    d="M6.04102 9.79167C5.69584 9.79167 5.41602 10.0715 5.41602 10.4167C5.41602 10.7618 5.69584 11.0417 6.04102 11.0417H13.9577C14.3028 11.0417 14.5827 10.7618 14.5827 10.4167C14.5827 10.0715 14.3028 9.79167 13.9577 9.79167H6.04102Z"
                                    fill="black"
                                    fill-opacity="0.6"
                                  />
                                  <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M1.66602 4.16667C1.66602 3.24619 2.41221 2.5 3.33268 2.5H16.666C17.5865 2.5 18.3327 3.24619 18.3327 4.16667V13.3333C18.3327 14.2538 17.5865 15 16.666 15H12.8118L10.7257 18.7088C10.4071 19.2752 9.5916 19.2752 9.27302 18.7088L7.18685 15H3.33268C2.41221 15 1.66602 14.2538 1.66602 13.3333V4.16667ZM7.18685 13.75C7.63844 13.75 8.05492 13.9936 8.27632 14.3872L9.99935 17.4503L11.7223 14.3872C11.9438 13.9936 12.3603 13.75 12.8118 13.75H16.666C16.8961 13.75 17.0827 13.5634 17.0827 13.3333V4.16667C17.0827 3.93655 16.8961 3.75 16.666 3.75H3.33268C3.10257 3.75 2.91602 3.93655 2.91602 4.16667V13.3333C2.91602 13.5634 3.10257 13.75 3.33268 13.75H7.18685Z"
                                    fill="black"
                                    fill-opacity="0.6"
                                  />
                                </svg>
                                <p className="text-xs font-semibold text-gray-600">
                                  그로우러닝 담당자
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-700 leading-6 whitespace-pre-wrap">
                                  {inquiry.comment}
                                </p>
                              </div>
                            </>
                          )}
                          {!inquiry.comment && (
                            <div className="border-t border-gray-300 pt-3">
                              <p className="text-sm text-gray-500 italic">
                                아직 답변이 없습니다.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="flex justify-center items-center py-12">
                <p className="text-gray-500">등록된 문의가 없습니다.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-6 overflow-x-auto pb-2">
              <button
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                disabled={currentPage === 1}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-8 h-8 flex items-center justify-center rounded text-xs md:text-sm font-medium transition-colors ${
                        currentPage === page
                          ? "bg-orange-500 text-white"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                onClick={() =>
                  setCurrentPage(Math.min(currentPage + 1, totalPages))
                }
                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                disabled={currentPage === totalPages}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Write Button - Desktop */}
          <div className="hidden md:flex justify-end mt-4">
            <button
              onClick={() => setShowNewInquiry(true)}
              className="px-6 py-3 bg-orange-500 text-white rounded hover:bg-orange-600 font-medium transition-colors"
            >
              글쓰기
            </button>
          </div>
        </div>
      )}

      {/* New Inquiry Form Modal */}
      {showNewInquiry && (
        <NewInquiryForm
          onClose={() => setShowNewInquiry(false)}
          onSuccess={() => {
            setShowNewInquiry(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};
